import React, { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";

/* ========= Popup Component ========= */
function StatusPopup({ type, onClose }) {
  const isSuccess = type === "success";

  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.85, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.85, y: 30 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#ffffff",
            borderRadius: "20px",
            padding: "48px 40px 40px",
            maxWidth: "420px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 24px 80px rgba(0,0,0,0.18)",
            position: "relative",
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: "72px",
              height: "72px",
              borderRadius: "50%",
              background: isSuccess ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
            }}
          >
            {isSuccess ? (
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="18" fill="#22c55e" />
                <path d="M10 18.5l5.5 5.5 10.5-11" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
                <circle cx="18" cy="18" r="18" fill="#ef4444" />
                <path d="M12 12l12 12M24 12L12 24" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            )}
          </div>

          <h3 style={{ fontSize: "20px", fontWeight: 700, color: "#1a1a1a", marginBottom: "10px" }}>
            {isSuccess ? "Enquiry Submitted!" : "Submission Failed"}
          </h3>

          <p style={{ fontSize: "14px", color: "#6b7280", lineHeight: 1.7, marginBottom: "28px" }}>
            {isSuccess
              ? "Thank you! Your enquiry has been received. Our admissions team will get back to you within 24 hours."
              : "Something went wrong while sending your enquiry. Please try again or contact us directly."}
          </p>

          {/* Auto-close progress bar */}
          <div style={{ height: "3px", background: "#f3f4f6", borderRadius: "2px", overflow: "hidden", marginBottom: "20px" }}>
            <motion.div
              initial={{ width: "100%" }}
              animate={{ width: "0%" }}
              transition={{ duration: 5, ease: "linear" }}
              style={{ height: "100%", background: isSuccess ? "#22c55e" : "#ef4444", borderRadius: "2px" }}
            />
          </div>

          <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
            <button
              onClick={onClose}
              style={{
                padding: "10px 28px",
                borderRadius: "30px",
                border: "none",
                background: isSuccess ? "#1E5AA8" : "#ef4444",
                color: "#fff",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {isSuccess ? "Done" : "Try Again"}
            </button>
            {!isSuccess && (
              <a
                href="tel:+919513597575"
                style={{
                  padding: "10px 28px",
                  borderRadius: "30px",
                  border: "1.5px solid #e5e7eb",
                  background: "transparent",
                  color: "#374151",
                  fontSize: "14px",
                  fontWeight: 600,
                  textDecoration: "none",
                  display: "inline-block",
                }}
              >
                Call Us
              </a>
            )}
          </div>

          <button
            onClick={onClose}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              color: "#9ca3af",
              fontSize: "20px",
              lineHeight: 1,
              padding: "4px",
            }}
          >
            ×
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

/* ========= Main Form ========= */
function Form() {
  const [loading, setLoading] = useState(false);
  const [popupType, setPopupType] = useState(null);

  /* ---- Validation errors ---- */
  const [errors, setErrors] = useState({});

  const formRef = useRef();
  const { ref: titleRef } = useInView({ threshold: 0.2, triggerOnce: true });
  const { ref: formViewRef, inView: formInView } = useInView({ threshold: 0.2, triggerOnce: true });
  const cardRef = useRef(null);

  useEffect(() => {
    if (formInView && cardRef.current) {
      gsap.fromTo(
        cardRef.current,
        { boxShadow: "0 0 0px rgba(30,90,168,0)" },
        { boxShadow: "0 12px 48px rgba(30,90,168,0.12)", duration: 1.1, ease: "power2.out", delay: 0.3 }
      );
    }
  }, [formInView]);

  /* ---- Focus → white border, Blur → clear ---- */
  const handleFocus = (e) =>
    gsap.to(e.target, { borderColor: "#ffffff", duration: 0.25 });

  const handleBlur = (e) =>
    gsap.to(e.target, { borderColor: "", duration: 0.25 });

  /* ---- Validation ---- */
  const validate = (data) => {
    const errs = {};

    if (!data.user_name.trim()) {
      errs.user_name = "Full name is required.";
    } else if (data.user_name.trim().length < 2) {
      errs.user_name = "Name must be at least 2 characters.";
    }

    if (!data.user_email.trim()) {
      errs.user_email = "Email address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.user_email)) {
      errs.user_email = "Please enter a valid email address.";
    }

    if (!data.user_phone.trim()) {
      errs.user_phone = "Phone number is required.";
    } else if (!/^[6-9]\d{9}$/.test(data.user_phone.replace(/\s/g, ""))) {
      errs.user_phone = "Enter a valid 10-digit Indian mobile number.";
    }

    if (!data.course) {
      errs.course = "Please select a course.";
    }

    return errs;
  };

  /* ---- Submit ---- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const form = formRef.current;
    const data = {
      user_name: form.user_name.value,
      user_email: form.user_email.value,
      user_phone: form.user_phone.value,
      course: form.course.value,
      message: form.message?.value || "",
    };

    const errs = validate(data);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setLoading(true);
    try {
      await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        form,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      setPopupType("success");
      form.reset();
      setErrors({});
    } catch (error) {
      console.error(error);
      setPopupType("error");
    }
    setLoading(false);
  };

  /* ---- Inline error style ---- */
  const errStyle = {
    color: "#ef4444",
    fontSize: "12px",
    marginTop: "4px",
    textAlign: "left",
    display: "block",
  };

  return (
    <>
      {popupType && <StatusPopup type={popupType} onClose={() => setPopupType(null)} />}

      <section id="contact" className="contact-area pt-120 pb-120" style={{ background: "#e7f0f8" }}>
        <div className="container">
          <div className="contact-bg" ref={cardRef}>

            <motion.div ref={titleRef} className="section-title text-center mb-50">
              <h2>Submit Your Enquiry</h2>
            </motion.div>

            <motion.form
              ref={(el) => { formRef.current = el; formViewRef(el); }}
              onSubmit={handleSubmit}
              className="contact-form mt-30 text-center"
              noValidate
            >
              <div className="row">

                {/* Name */}
                <div className="col-lg-4">
                  <div className="contact-field mb-30">
                    <input
                      type="text"
                      name="user_name"
                      placeholder="Full Name"
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      style={errors.user_name ? { borderColor: "#ef4444" } : {}}
                      onChange={() => setErrors((p) => ({ ...p, user_name: "" }))}
                    />
                    {errors.user_name && <span style={errStyle}>⚠ {errors.user_name}</span>}
                  </div>
                </div>

                {/* Email */}
                <div className="col-lg-4">
                  <div className="contact-field mb-30">
                    <input
                      type="email"
                      name="user_email"
                      placeholder="Email Address"
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      style={errors.user_email ? { borderColor: "#ef4444" } : {}}
                      onChange={() => setErrors((p) => ({ ...p, user_email: "" }))}
                    />
                    {errors.user_email && <span style={errStyle}>⚠ {errors.user_email}</span>}
                  </div>
                </div>

                {/* Phone */}
                <div className="col-lg-4">
                  <div className="contact-field mb-30">
                    <input
                      type="text"
                      name="user_phone"
                      placeholder="Phone Number"
                      maxLength={10}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      style={errors.user_phone ? { borderColor: "#ef4444" } : {}}
                      onChange={() => setErrors((p) => ({ ...p, user_phone: "" }))}
                    />
                    {errors.user_phone && <span style={errStyle}>⚠ {errors.user_phone}</span>}
                  </div>
                </div>

                {/* Course */}
                <div className="col-lg-12">
                  <div className="contact-field mb-30">
                    <select
                      name="course"
                      className="custom-select"
                      style={errors.course ? { borderColor: "#ef4444" } : {}}
                      onChange={() => setErrors((p) => ({ ...p, course: "" }))}
                    >
                      <option value="">Select Course</option>
                      <option value="Bachelor of Pharmacy (B.Pharm)">
                        Bachelor of Pharmacy (B.Pharm)
                      </option>
                    </select>
                    {errors.course && <span style={errStyle}>⚠ {errors.course}</span>}
                  </div>
                </div>

                {/* Message */}
                <div className="col-lg-12">
                  <div className="contact-field mb-30">
                    <textarea
                      name="message"
                      rows="6"
                      placeholder="Please share your enquiry, admission interest, or any questions about the B.Pharm program."
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                  </div>

                  <input type="hidden" name="website" value="Pharmacy Website" />

                  <div className="text-center">
                    <button
                      className="btn ss-btn"
                      type="submit"
                      disabled={loading}
                      style={{ opacity: loading ? 0.75 : 1 }}
                    >
                      {loading ? (
                        <span style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
                          <svg width="16" height="16" viewBox="0 0 16 16" style={{ animation: "spin 0.8s linear infinite" }}>
                            <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none" />
                            <path d="M8 2a6 6 0 0 1 6 6" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" />
                          </svg>
                          Sending...
                        </span>
                      ) : "Submit Enquiry"}
                    </button>
                  </div>
                </div>

              </div>
            </motion.form>
          </div>
        </div>
      </section>

      <style>{`
        /* ---- Spinner ---- */
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ---- White border on focus (overrides GSAP for all inputs) ---- */
        .contact-field input:focus,
        .contact-field textarea:focus {
          border-color: #ffffff !important;
          outline: none;
        }

        /* ---- Override browser autofill background ---- */
        .contact-field input:-webkit-autofill,
        .contact-field input:-webkit-autofill:hover,
        .contact-field input:-webkit-autofill:focus,
        .contact-field input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
          box-shadow: 0 0 0px 1000px #ffffff inset !important;
          -webkit-text-fill-color: #333333 !important;
          background-color: #ffffff !important;
          border-color: #ffffff !important;
          transition: background-color 99999s ease-in-out 0s;
        }

        /* ---- Custom select with clean chevron arrow ---- */
        .contact-field .custom-select {
          width: 100%;
          height: 55.8px;
          padding: 0 40px 0 20px;
          color: #7f7873;
          font-size: 16px;
          font-weight: 400;
          border: 1px solid rgba(255,255,255,0.6);
          border-radius: 6px;
          background-color: #fff;
          appearance: none;
          -webkit-appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%237f7873' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'%3E%3C/path%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: calc(100% - 12px) center;
          background-size: 20px;
          cursor: pointer;
          transition: border-color 0.25s ease;
        }

        .contact-field .custom-select:focus {
          border-color: #ffffff !important;
          outline: none;
        }
      `}</style>
    </>
  );
}

export default Form;
